import { useEffect, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { Formik, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
  MultipleSelectionInput,
  AllowedMembersAlert,
  TagsInput,
  MemberMultiSelectDropdown,
} from '../common';
import { LOCAL_STORAGE_USER_DATA } from '../../constants';
import { fetchOnBoardProgress } from '../../actions/user';
import { teamService } from '../../services';
import { fetchTeamMembers } from '../../actions/team';
import { fetchActiveProjects } from '../../actions/projects';
import { getUserData } from '../../helpers';
import { sendAmplitudeData } from '../../utilities/amplitude.js';

function AddMemberModal({ showInviteModal, toggleShowInviteModal }) {
  const [isSubmitting, setSubmitting] = useState(false);
  const handleClose = () => toggleShowInviteModal();
  const handleShow = () => toggleShowInviteModal();
  const teamOwnerId = localStorage.getItem('teamOwnerId');
  const existingTeamMembers = [];
  const newTeamMembers = [];
  const dispatch = useDispatch();
  const teamData = useSelector((state) => state.team);
  const allProjects = useSelector((state) => state.allProjects);
  const projectsList = allProjects.projects || [];
  const teamList = teamData.members || [];

  // Filtered inactive members and team Owner
  const activeMembersList = teamList.filter(function (item) {
    return item.status !== 'inactive' && item.role !== 'creator';
  });
  const userData = JSON.parse(localStorage.getItem(LOCAL_STORAGE_USER_DATA));
  useEffect(() => {
    dispatch(fetchActiveProjects());
    dispatch(fetchTeamMembers());
  }, []);
  const updateTeamInvitations = (formdata) => {
    setSubmitting(true);
    const existingMembers = [...formdata.addExistingTeamMembers];
    const newMembers =
      [...formdata.addNewTeamMembers].length > 0
        ? [...formdata.addNewTeamMembers].map((newInvitee) => newInvitee.value)
        : [];
    const inviteesList = [...existingMembers, ...newMembers];
    if (inviteesList.length <= 0) {
      setSubmitting(false);
      toast.error('Please enter Email address or select existing Team member');
    } else if (formdata.projects.length <= 0) {
      setSubmitting(false);
      toast.error('Please select project');
    } else {
      let toastMessage = '';
      teamService
        .update_team_invitations({
          teamOwnerId,
          projectId: JSON.stringify(formdata.projects),
          invites: JSON.stringify(inviteesList),
        })
        .then((response) => {
          if (userData.subscriptionDetails.currentPlan === 'trial') {
            dispatch(fetchOnBoardProgress());
            sendAmplitudeData('Invited member to project', {
              page: 'Member invite team active',
              status: 'Success',
            });
          }
          setSubmitting(false);
          toggleShowInviteModal();
          if (response.message === 'Success') {
            if (response.data && response.data.SentInvites)
              toast.success(
                <div>
                  {response.data.SentInvites.map((item) => (
                    <p>{item}</p>
                  ))}
                </div>
              );
            if (response.data && response.data.ExistingInvites)
              toast.error(
                <div>
                  {response.data.ExistingInvites.map((item) => (
                    <p>{item}</p>
                  ))}
                </div>
              );
          } else if (response.data && response.data.ExistingInvites) {
            toast.error(
              <div>
                {response.message}
                <br />{' '}
                {response.data.ExistingInvites.map((item) => (
                  <p>{item}</p>
                ))}
              </div>
            );
          } else {
            toast.error(response.message);
          }
        })
        .catch((response) => {
          if (response.message === 'Success') {
            toastMessage = response.message;
            if (response.data && response.data.SentInvites)
              toast.success(
                <div>
                  {toastMessage}
                  <br />
                  {response.data.SentInvites.map((item) => (
                    <p>{item}</p>
                  ))}
                </div>
              );
            if (response.data && response.data.ExistingInvites)
              toast.error(
                <div>
                  {toastMessage}
                  <br />
                  {response.data.ExistingInvites.map((item) => (
                    <p>{item}</p>
                  ))}
                </div>
              );
          } else if (response.data && response.data.ExistingInvites) {
            toast.error(
              <div>
                {response.message}
                <br />{' '}
                {response.data.ExistingInvites.map((item) => (
                  <p>{item}</p>
                ))}
              </div>
            );
          } else {
            toast.error(response.message);
          }

          setSubmitting(false);
          toggleShowInviteModal();
        });
    }
  };
  const maxMembersAllowed = getUserData('maxMembersAllowed') || 0;
  return (
    <>
      <button
        className="btn btn-primary lift mb-1 me-2"
        onClick={handleShow}
        title="view all"
        type="button"
      >
        Add Member
      </button>
      <Modal
        show={showInviteModal}
        onHide={handleClose}
        animation={false}
        size="md"
      >
        <Modal.Header>
          <Modal.Title className="m-0">Invite Team Members</Modal.Title>
          <button
            type="button"
            className="btn-close"
            aria-label="Close"
            onClick={handleClose}
          />
        </Modal.Header>
        <Formik
          enableReinitialize
          validateOnMount
          initialValues={{
            addExistingTeamMembers: existingTeamMembers,
            addNewTeamMembers: newTeamMembers,
            projects: '',
          }}
          validationSchema={Yup.object({
            projects: Yup.array().required('Please select Project'),
          })}
          onSubmit={(values) => {
            updateTeamInvitations(values);
          }}
        >
          {(formikprops) => (
            <Form
              onSubmit={formikprops.handleSubmit}
              className="needs-validation"
            >
              {projectsList.length > 0 ? (
                <>
                  <Modal.Body>
                    <AllowedMembersAlert />
                    <section>
                      <MemberMultiSelectDropdown
                        id="selectInputRef"
                        name="addExistingTeamMembers"
                        labelText="Add existing Team Member(s)"
                        fromArray={activeMembersList}
                        formProps={formikprops}
                        isDisabled={activeMembersList.length < 1 && true}
                      />
                      {maxMembersAllowed - activeMembersList.length > 0 && (
                        <TagsInput
                          id="tagInputRef"
                          name="addNewTeamMembers"
                          labelText="Invite new Team Member(s)"
                          placeHolderText="Type email address and press enter"
                          formProps={formikprops}
                        />
                      )}
                    </section>
                    <Form.Group controlId="choices-multiple-default">
                      <MultipleSelectionInput
                        id="add-projects"
                        name="projects"
                        labelText="Projects"
                        fromArray={projectsList}
                        formProps={formikprops}
                        isDisabled={projectsList.length < 1 && true}
                        requiredFiled
                      />
                      <ErrorMessage
                        name="projects"
                        render={(msg) => (
                          <small className="text-danger">{msg}</small>
                        )}
                      />
                    </Form.Group>
                  </Modal.Body>

                  <Modal.Footer className="d-flex justify-content-between">
                    <Button variant="link" onClick={handleClose}>
                      Close
                    </Button>
                    <Button
                      variant="primary"
                      type="submit"
                      disabled={
                        !(formikprops.isValid && formikprops.dirty) ||
                        isSubmitting ||
                        formikprops.values.projects.length < 1 ||
                        (formikprops.values.addNewTeamMembers.length < 1 &&
                          formikprops.values.addExistingTeamMembers.length < 1)
                      }
                    >
                      {isSubmitting && (
                        <span
                          className="spinner-border spinner-border-sm me-1"
                          role="status"
                          aria-hidden="true"
                        />
                      )}
                      Invite
                    </Button>
                  </Modal.Footer>
                </>
              ) : (
                <div className="card">
                  <div className="card-body d-flex justify-content-left">
                    No Projects to add Members
                  </div>
                </div>
              )}
            </Form>
          )}
        </Formik>
      </Modal>
    </>
  );
}
AddMemberModal.propTypes = {
  showInviteModal: PropTypes.bool,
  toggleShowInviteModal: PropTypes.func,
};
export default AddMemberModal;
